import React, { useEffect, useMemo, useCallback } from "react";
import { MapContainer, Marker } from "react-leaflet";
import MapMarker from "../Map/MapMarker";
import MapTiles from "../MapTiles/MapTiles";
import { createCategoryIcon, createUserIcon } from "../../util/mapStyles";
import { useUIContext } from "../../context/UIContext/UIContext";

const MiniMap = ({ event, userLocation, style }) => {
  const mapRef = React.useRef();
  const { darkMode } = useUIContext();

  // Memoize map center to prevent array recreation
  const center = useMemo(() => {
    if (event?.location) {
      return [event.location.coordinates[1], event.location.coordinates[0]];
    }
    if (userLocation) {
      return [userLocation.lat, userLocation.lng];
    }
    return [0, 0];
  }, [event?.location?.coordinates, userLocation?.lat, userLocation?.lng]);

  // Memoize marker position to prevent array recreation
  const markerPosition = useMemo(() => {
    return event?.location 
      ? [event.location.coordinates[1], event.location.coordinates[0]] 
      : [0, 0];
  }, [event?.location?.coordinates]);

  // Memoize user position to prevent array recreation
  const userPosition = useMemo(() => {
    return userLocation ? [userLocation.lat, userLocation.lng] : null;
  }, [userLocation?.lat, userLocation?.lng]);

  // Memoize icons to prevent recreation on every render
  const eventIcon = useMemo(() => createCategoryIcon(event?.category), [event?.category]);
  const userIcon = useMemo(() => createUserIcon(), []);

  // Memoize maxBounds (static, never changes)
  const maxBounds = useMemo(() => [
    [-90, -180], // southwest corner of the world
    [90, 180], // northeast corner of the world
  ], []);

  // Memoize whenReady callback
  const handleMapReady = useCallback((map) => {
    mapRef.current = map.target;
  }, []);

  useEffect(() => {
    if (mapRef.current && event?.location) {
      mapRef.current.setView([event.location.coordinates[1], event.location.coordinates[0]]);
    }
  }, [event?.location?.coordinates]);

  return (
    <MapContainer
      key={event?._id}
      attributionControl={false}
      style={style}
      whenReady={handleMapReady}
      center={center}
      zoom={13}
      minZoom={10}
      className="mini-map-container"
      zoomControl={false}
      maxBounds={maxBounds}
    >
      <MapTiles darkMode={darkMode} />
      <MapMarker
        icon={eventIcon}
        position={markerPosition}
        title={event?.name}
      />
      {userPosition && (
        <Marker
          icon={userIcon}
          position={userPosition}
        />
      )}
    </MapContainer>
  );
};

// Memoize MiniMap to prevent unnecessary re-renders
export default React.memo(MiniMap, (prevProps, nextProps) => {
  return (
    prevProps.event?._id === nextProps.event?._id &&
    prevProps.event?.category === nextProps.event?.category &&
    prevProps.event?.location?.coordinates?.[0] === nextProps.event?.location?.coordinates?.[0] &&
    prevProps.event?.location?.coordinates?.[1] === nextProps.event?.location?.coordinates?.[1] &&
    prevProps.userLocation?.lat === nextProps.userLocation?.lat &&
    prevProps.userLocation?.lng === nextProps.userLocation?.lng &&
    prevProps.style === nextProps.style
  );
});
